import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import { IUpdateDto } from "@/lib/utility/data/update-dto.interface";
import { $t } from "@/lib/utility/t";
import { TagGoToHelper } from "@/lib/utility/tag.go-to-helper";
import tagService from "@/services/mrfiktiv/services/tag.service";
import {
  MrfiktivCreateTagDtoGen,
  MrfiktivTagViewModelGen,
  MrfiktivUpdateTagDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { TagDataAccessLayer } from "@/store/modules/access-layers/tag.access-layer";
import Vue from "vue";
import VueRouter from "vue-router";
import { ITimestamp, Timestamp } from "./timestamp.entity";

@IsFilterable
export class TagBase implements Omit<MrfiktivTagViewModelGen, "resources">, ICreateDto<ITag>, IUpdateDto<ITag> {
  id: string;

  partnerId: string;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.tag.value"
  })
  value: string;
  readonly maxLength = 32;

  icon?: string;

  color?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.tag.description"
  })
  description?: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.tag.resources",
    config: {
      items: [{ text: $t("enums.ResourceEnum.COMPANY"), value: BackendResourceEnum.COMPANY }],
      itemValue: "value"
    }
  })
  resources: BackendResourceEnum[];

  loading = false;

  get availableResources(): BackendResourceEnum[] {
    return [BackendResourceEnum.COMPANY, BackendResourceEnum.DOCUMENT, BackendResourceEnum.TICKET];
  }

  get titleReadable() {
    return this.value;
  }

  get colors() {
    return [
      "#90a4ae", // blue-grey lighten-3
      "#64b5f6", // blue lighten-3
      "#81c784", // green lighten-3
      "#d4e157", // lime lighten-3
      "#fbc02d", // yellow darken-1
      "#ffcc80", // orange lighten-3
      "#e57373", // red lighten-3
      "#f06292", // pink lighten-3
      "#ba68c8" // purple lighten-3
    ];
  }

  constructor(tag?: Partial<TagBase | MrfiktivTagViewModelGen>) {
    this.id = tag?.id ?? "";
    this.partnerId = tag?.partnerId ?? "";
    this.timestamp = new Timestamp(tag?.timestamp);
    this.value = tag?.value ?? "";
    this.icon = tag?.icon;
    this.color = tag?.color;
    this.description = tag?.description;
    this.resources = (tag?.resources ?? []) as BackendResourceEnum[];

    if (this.resources.length === 0) {
      this.resources = this.availableResources;
    }
  }

  async fetch(): Promise<this> {
    this.loading = true;

    try {
      const res = await tagService.getOne(this.partnerId, this.id);
      this.map(res);
      TagDataAccessLayer.set(this);
    } catch (e) {
      Vue.$log.error(e);
    } finally {
      this.loading = false;
    }

    return this;
  }

  map(tag?: MrfiktivTagViewModelGen) {
    if (!tag) return;
    this.id = tag?.id ?? "";
    this.partnerId = tag?.partnerId ?? "";
    this.timestamp = new Timestamp(tag?.timestamp);
    this.value = tag?.value ?? "";
    this.icon = tag?.icon;
    this.color = tag?.color;
    this.description = tag?.description;
    this.resources = (tag?.resources ?? []) as BackendResourceEnum[];

    if (this.resources.length === 0) {
      this.resources = this.availableResources;
    }
  }

  async create() {
    const resourcesForBackend = this.resources.length === this.availableResources.length ? [] : this.resources;

    const data: MrfiktivCreateTagDtoGen = {
      value: this.value,
      icon: this.icon || undefined,
      color: this.color || undefined,
      description: this.description || undefined,
      resources: resourcesForBackend as any
    };
    const res = await tagService.create(this.partnerId, data);

    this.map(res);
    TagDataAccessLayer.set(this);

    return this;
  }

  async delete() {
    const res = await tagService.delete(this.partnerId, this.id);
    this.map(res);
    TagDataAccessLayer.delete(this);
  }

  async update() {
    const resourcesForBackend = this.resources.length === this.availableResources.length ? [] : this.resources;

    const data: MrfiktivUpdateTagDtoGen = {
      value: this.value,
      icon: this.icon || undefined,
      color: this.color || undefined,
      description: this.description || undefined,
      resources: resourcesForBackend as any
    };
    const res = await tagService.update(this.partnerId, this.id, data);
    this.map(res);
    TagDataAccessLayer.set(this);

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateTagDtoGen) {
    if (dto.resources) {
      dto.resources = dto.resources.length === this.availableResources.length ? [] : dto.resources;
    }

    const res = await tagService.update(this.partnerId, this.id, dto);
    this.map(res);
    TagDataAccessLayer.set(this);

    return this;
  }

  get isUpdateable() {
    return true;
  }

  goTo(router: VueRouter) {
    return {
      table: () =>
        new TagGoToHelper(router).goToTagTable({
          partnerId: this.partnerId
        }),
      detail: () =>
        new TagGoToHelper(router).goToTagDetail({
          partnerId: this.partnerId,
          tagId: this.id
        })
    };
  }
}

type ITag = TagBase;
const Tag = Filter.createForClass(TagBase);

export { ITag, Tag };
